import { configuredLanguage } from "./tutorials/language";
import fa from "./content/fa";

// The website language is fixed at build time by REACT_APP_DEFAULT_LANGUAGE.
// UI strings are written in English in the components and wrapped in t(); the
// English text is the lookup key, so a missing translation falls back to English.
export const language = configuredLanguage;
export const isPersian = language === "fa";
export const direction = isPersian ? "rtl" : "ltr";
const locale = isPersian ? "fa-IR" : "en";

const dictionaries = { fa };

export function formatNumber(value, options) {
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value ?? "");
  return new Intl.NumberFormat(locale, options).format(number);
}

export function t(text, values) {
  const template = dictionaries[language]?.[text] ?? text;
  if (!values) return template;
  return Object.entries(values).reduce(
    (result, [key, value]) => result.replaceAll(`{{${key}}}`, typeof value === "number" ? formatNumber(value) : value),
    template,
  );
}

export function formatDate(value, options = { month: "short", day: "numeric", year: "numeric" }) {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, options).format(date);
}

// Returns the price as a display value and a separate unit, so callers can style them apart.
// Persian pages show Iranian prices in Toman (stored in Rial, 1 Toman = 10 Rial).
export function priceParts(price) {
  if (!price) return { value: "", unit: "" };
  const amount = Number(price.amount || 0);
  const currency = String(price.currency || "USD").toUpperCase();

  if (currency === "IRR" && isPersian) {
    return { value: formatNumber(Math.round(amount / 10)), unit: t("Toman") };
  }

  try {
    return {
      value: new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      }).format(amount),
      unit: "",
    };
  } catch {
    return { value: formatNumber(amount), unit: currency };
  }
}

export function formatPrice(price) {
  const { value, unit } = priceParts(price);
  return unit ? `${value} ${unit}` : value;
}

export function applyDocumentLanguage(doc = document) {
  doc.documentElement.lang = language;
  doc.documentElement.dir = direction;
  const brand = process.env.REACT_APP_BRAND_TITLE?.trim() || t("Pocket Play");
  doc.title = isPersian ? t("Pocket Play") : brand;
  doc.querySelector('meta[name="description"]')?.setAttribute(
    "content",
    t("Big-screen party games where every phone becomes a controller."),
  );
}
